import { useCallback, useContext, useEffect, useState } from "react";
import {
    Button,
    Form,
    FormGroup, Icon, Label,
    Modal,
    ModalActions,
    Placeholder,
    PlaceholderLine,
    PlaceholderParagraph,
    Segment,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from "semantic-ui-react";
import { useStore } from "../../app/stores/store";
import { formatCurrency, getFloatValue } from "../../shared/utils";
import { InvestmentInterest } from "../../app/models/Investment/InvestmentInterest";
import EntityContext from "../../app/context/entityContext";
import { toast } from "react-toastify";
import React from "react";
import { InvestmentInterestGrid } from "../../app/models/Investment/InvestmentInterestGrid";
import { Formik } from "formik";
import * as yup from 'yup';
import SelectInput from "../../app/common/form/SelectInput";
import CurrencyInput from "../../app/common/form/CurrencyInput";
import { InvestmentShareInfo } from "../../app/models/Investment/InvestmentShareInfo";
import ConfirmButton from "../../shared/ConfirmButton";

interface Params {
    show: boolean,
    investmentId: number,
    prefillShareClassId: number,
    onAccept: (interestCount: number) => void;
    onCancel: () => void;
}

function InvestmentInterestPanel({ show, investmentId, prefillShareClassId, onAccept, onCancel }: Params) {

    const [loading, setLoading] = useState(true);
    const { commonStore, investmentStore } = useStore();
    const { entity } = useContext(EntityContext);
    const { investmentInsightsStore } = useStore();
    // interestList holds the interests that will be submitted
    const [interestList, setInterestList] = useState<InvestmentInterest[]>([]);
    // interestGrid holds the interests in user facing display
    const [interestGrid, setInterestGrid] = useState<InvestmentInterestGrid[]>([])

    const emptyOption = { key: 0, value: 0, text: '' };
    const [accreditationTypes, setAccreditationTypes] = useState([emptyOption]);
    const [fundingMethods, setFundingMethods] = useState([emptyOption]);

    const [shareInfoOptions, setShareInfoOptions] = useState([emptyOption]);
    const [currentInterest, setCurrentInterest] = useState(new InvestmentInterest());
    const [isDirty, setIsDirty] = useState(false);
    const [shareInfo, setShareInfo] = useState<InvestmentShareInfo[]>([]);
    // Temp storage for UI updates
    const [selectedShareClass, setSelectedShareClass] = useState<number | undefined>();
    const [shareDollarAmount, setShareDollarAmount] = useState(0.0);
    const [shareTotalAmount, setShareTotalAmount] = useState(0);

    const updateShareTotal = useCallback((enteredCount: number | null) => {
        let total = shareDollarAmount * (enteredCount ?? 0);
        setShareTotalAmount(total);
    }, [shareDollarAmount]);

    useEffect(() => {
        setLoading(true);
        investmentStore.getInvestmentInterests(entity.id, investmentId)
            .then((r) => {
                setInterestList(r);
            })

            .then(() => {

                commonStore.getInvestmentEnums()
                    .then(r => {

                        let l = r.accreditationTypeEnums.map((a: { key: any; value: any; text: any }) => ({ key: a.key, value: a.key, text: a.text }));
                        setAccreditationTypes(
                            l.sort((a: { text: any; }, b: { text: any; }) => String(a.text).localeCompare(String(b.text)))
                        );
                    })
            })
            .then(() => {
                investmentStore.getInvestmentShareInfo(investmentId)
                    .then(r => {
                        setShareInfo(r);
                        setShareInfoOptions(r.map(s => ({ key: s.id, value: s.id, text: s.name }))
                        );
                        // Prefill the Share Class selection from constructor value
                        if (prefillShareClassId > 0) {
                            setSelectedShareClass(prefillShareClassId);
                            let i = new InvestmentInterest();
                            i.investmentShareClassificationId = prefillShareClassId;
                            setCurrentInterest(i);
                        }
                    })
            })
            .then(() => {
                investmentStore.getInvestmentFundingMethods(investmentId)
                    .then(r => {
                        let f = r.map(s => ({ key: s.key, value: s.key, text: s.value }));
                        setFundingMethods(
                            f.sort((a: { text: any; }, b: { text: any; }) => String(a.text).localeCompare(String(b.text)))
                        );
                    })
            })
            .catch(() => {
                toast.error("Unable to get investment information.", { theme: "colored" });
            })
            .finally(() => {
                setLoading(false)
            });

    }, [entity.id, investmentId, investmentInsightsStore, investmentStore, commonStore, prefillShareClassId]);

    useEffect(() => {
        if (selectedShareClass === undefined) { return; }
        let shareValue = shareInfo.find((s) => s.id === selectedShareClass)?.shareValue ?? 0.0;
        setShareDollarAmount(shareValue);
    }, [selectedShareClass, shareInfo]);

    const updateInterestGrid = useCallback(() => {
        setInterestGrid(interestList.map(i => ({
            fundingMethodDescription: fundingMethods?.find(v => v.key === i.fundingMethod)?.text as string,
            accreditationDescription: accreditationTypes?.find(v => String(v.key) === String(i.accreditation))?.text as string,
            shareClass: shareInfoOptions?.find(v => v.key === i.investmentShareClassificationId)?.text as string,
            shareCount: i.shareCount,
            amount: i.amount,
            // Not displayed at this time
            id: 0,
            approved: false,
            entityTypeDescription: '',
            email: '',
            entityName: '',
            cellPhone: '',
            fundingStatus: 0,
            fundingStatusDescription: '',
        })))
    }, [accreditationTypes, fundingMethods, interestList, shareInfoOptions])

    useEffect(() => {
        updateInterestGrid();
    }, [interestList, updateInterestGrid])



    const addInterest = useCallback((shareClass: number, shareCount: number | null, funding: number, accreditation: number) => {
        let item = new InvestmentInterest();
        let amount = shareInfo.find((s) => s.id === shareClass)?.shareValue ?? 0.0;

        item.entityId = entity.id;
        item.investmentId = investmentId;
        item.investmentShareClassificationId = shareClass;
        item.shareCount = shareCount;
        item.amount = amount * (shareCount ?? 0);
        item.fundingMethod = funding;
        item.accreditation = accreditation;

        setInterestList([...interestList, item]);
        setIsDirty(true);
    }, [entity.id, interestList, investmentId, shareInfo]);

    const removeInterest = useCallback((idx: number) => {
        setInterestList(interestList.filter((v, i) => i !== idx));
        setIsDirty(true);
    }, [interestList])

    const saveInterests = useCallback(() => {
        investmentStore.saveInvestmentInterests(entity.id, investmentId, interestList)
            .then(r => {
                toast.success("Interests submitted", { theme: 'colored' })

            })
            .then(r => {
                onAccept(interestList.length);
            })
            .catch(r => {
                toast.error("An error occurred while submitting the interests", { theme: 'colored' })
            })
    }, [interestList, investmentStore, onAccept, investmentId, entity.id])


    const validationSchema = yup.object({
        shareCount: yup
            .number()
            .required('# of shares must be specified')
            .min(1, 'At least 1 share is required. ')
            .transform((_value, originalValue) => getFloatValue(originalValue)),
        investmentShareClassificationId: yup
            .number()
            .required('Share class selection is required')
            .min(1, 'Share class must be selected'),
        fundingMethod: yup
            .number()
            .required('Funding method selection is required')
            .min(1, 'Funding method must be selected'),
        accreditation: yup
            .number()
            .required('Accreditation selection is required.')
            .min(1, 'Accreditation must be selected'),
    })
    const handleFormSubmit = async (values: any, setSubmitting: any) => {
        let item = new InvestmentInterest();
        let amount = shareInfo.find(i => i.id === values.shareClass)?.shareValue ?? 0.0;
        item.investmentShareClassificationId = values.investmentShareClassificationId;
        item.shareCount = values.shareCount;
        item.amount = amount * values.shareCount;
        item.fundingMethod = values.fundingMethod;
        item.accreditation = values.accreditation;
        addInterest(
            item.investmentShareClassificationId,
            item.shareCount,
            item.fundingMethod,
            item.accreditation
        );
        setCurrentInterest(new InvestmentInterest());
        setSubmitting(false);
    }
    return (
        <>
            <Segment clearing loading={loading}>
                <Modal open={show} centered={true} closeOnEscape={false} closeOnDimmerClick={false} size='large'>
                    <Modal.Header>Investment Interest</Modal.Header>
                    <Modal.Content>
                        {loading ?
                            <Placeholder>
                                <PlaceholderParagraph>
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                    <PlaceholderLine />
                                </PlaceholderParagraph>
                            </Placeholder>
                            :
                            <>
                                {interestGrid.length > 0 &&
                                    <Table color='green' striped>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHeaderCell>Share Class</TableHeaderCell>
                                                <TableHeaderCell># of Shares</TableHeaderCell>
                                                <TableHeaderCell>$ Amount</TableHeaderCell>
                                                <TableHeaderCell>Funding Method</TableHeaderCell>
                                                <TableHeaderCell>Accreditation</TableHeaderCell>
                                                <TableHeaderCell>Remove</TableHeaderCell>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {interestGrid.map((s, i) => {
                                                return (
                                                    <TableRow key={i}>
                                                        <TableCell>{s.shareClass}</TableCell>
                                                        <TableCell>{s.shareCount}</TableCell>
                                                        <TableCell>{formatCurrency(s.amount)}</TableCell>
                                                        <TableCell>{s.fundingMethodDescription}</TableCell>
                                                        <TableCell>{s.accreditationDescription}</TableCell>
                                                        <TableCell collapsing textAlign={'center'}>
                                                            <ConfirmButton id={i} color='red' iconName='trash alternate' basic
                                                                content={'Remove investment pledge?'}
                                                                value='' confirmButtonText='Remove'
                                                                handleConfirm={() => removeInterest(i)} />
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                }
                                <Formik
                                    validationSchema={validationSchema}
                                    enableReinitialize
                                    initialValues={currentInterest}
                                    onSubmit={(values, { setSubmitting }) => {
                                        handleFormSubmit(values, setSubmitting);
                                    }}>
                                    {({ handleSubmit, isSubmitting }) => (
                                        <Form id='interestform' className="ui form" onSubmit={handleSubmit} autoComplete='Off'>
                                            <FormGroup widths='equal'>
                                                <SelectInput
                                                    placeholder='Select Share Class'
                                                    options={shareInfoOptions}
                                                    name='investmentShareClassificationId'
                                                    showRequired
                                                    onChange={(e, i) => { setSelectedShareClass(i.value) }} />
                                                <CurrencyInput placeholder={`# of Shares (${formatCurrency(shareTotalAmount)} @ ${formatCurrency(shareDollarAmount)}/share) `}
                                                    name='shareCount' thousandSeparator=','
                                                    decimalScale={0} allowNegative={false} maxLength={16}
                                                    showRequired
                                                    onChange={(e) => updateShareTotal(e)}
                                                />

                                            </FormGroup>
                                            <FormGroup widths='equal'>
                                                <SelectInput
                                                    placeholder='Select funding method'
                                                    options={fundingMethods}
                                                    name='fundingMethod'
                                                    showRequired />
                                                <SelectInput
                                                    placeholder='Select accreditation'
                                                    options={accreditationTypes}
                                                    name='accreditation'
                                                    showRequired />
                                            </FormGroup>
                                        </Form>
                                    )}

                                </Formik>
                            </>
                        }
                    </Modal.Content>
                    <ModalActions>
                        <Label icon color={'green'}>
                            <Icon name={'info circle'}></Icon>
                            This is a pledged expression of interest only. You will not be charged, and no transfer of funds will occur when you click Save.
                        </Label>
                        <Button disabled={loading} loading={loading} type='button' content='Close' onClick={onCancel} />
                        <Button disabled={loading} loading={loading} form='interestform' primary type='submit' content='Add Interest' />
                        <Button color='green' disabled={loading || !isDirty}
                            content='Save'
                            onClick={() => {
                                saveInterests();
                            }} />

                    </ModalActions>
                </Modal>
            </Segment >

        </>
    )
}

export default InvestmentInterestPanel